"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, Check } from "lucide-react";
import client from "@/lib/api/client";
import { _ } from "@/lib/api/i18n";
import { useLayout } from "@/lib/context/layout";

interface ContextEntry {
    name: string;
    id: string;
    provider: string;
    reseller?: string;
    client?: string;
    icon?: string;
    properties: Record<string, unknown>;
    clients?: Record<string, ContextEntry>;
}

interface TreeNode {
    key: string;
    label: string;
    data: string[];
    properties: Record<string, unknown>;
    icon_herf?: string;
    icon_class: string;
    children: TreeNode[];
}

interface SelectedPath {
    label: string;
    icon?: string;
}

const findNode = (nodes: TreeNode[], keys: string[]): TreeNode | null => {
    if (keys.length === 0) return null;
    const [first, ...rest] = keys;
    const found = nodes.find((n) => n.data.join("#") === keys.join("#"));
    if (!found) return null;
    if (rest.length === 0) return found;
    return findNode(found.children, rest);
};

const flattenNodes = (nodes: TreeNode[], depth = 0): { node: TreeNode; depth: number }[] => {
    const result: { node: TreeNode; depth: number }[] = [];
    for (const n of nodes) {
        result.push({ node: n, depth });
        if (n.children.length > 0) {
            result.push(...flattenNodes(n.children, depth + 1));
        }
    }
    return result;
};

export const ContextElement = () => {
    const { activeContext, setActiveContext, setEnableDashboards, setLayoutState, refreshContext } = useLayout();
    const [state, setState] = useState<{
        ready: boolean;
        loading: boolean;
        data?: Record<string, ContextEntry>;
        selected?: string;
    }>({ ready: false, loading: true });

    useEffect(() => {
        console.log("[ContextSelector] calling get_contexts...");
        client.get_contexts().then((ctx: Record<string, ContextEntry> | "err" | undefined) => {
            console.log("[ContextSelector] get_contexts result:", ctx);
            if (!ctx || ctx === "err") {
                console.log("[ContextSelector] no context, enabling dashboards anyway");
                setEnableDashboards(true);
                return;
            }
            let act_context = client.load_context();
            if (!act_context) {
                let first: ContextEntry;
                if (ctx instanceof Array) {
                    first = ctx[0];
                } else {
                    first = ctx[Object.keys(ctx)[0]];
                }
                act_context = [first.provider, first.reseller || "", first.client || ""];
            }
            let cur_ctx: ContextEntry | null = null;
            let dept = 0;
            let _ctx: Record<string, ContextEntry> | undefined = ctx;
            while (_ctx) {
                dept++;
                _ctx = _ctx[Object.keys(_ctx)[0]].clients;
            }
            const ctxRecord = ctx as Record<string, ContextEntry>;
            if (act_context) {
                if (!act_context[0]) {
                    act_context[0] = "everyware";
                }
                if (dept > 2) {
                    cur_ctx = ctxRecord[act_context[0]];
                } else {
                    cur_ctx = ctxRecord as unknown as ContextEntry;
                }
            }
            if (act_context[1]) {
                if (dept > 1) {
                    if (dept === 2) {
                        cur_ctx = (cur_ctx as unknown as Record<string, ContextEntry>)[act_context[1]];
                    } else {
                        cur_ctx = (cur_ctx as ContextEntry).clients![act_context[1]];
                    }
                } else {
                    cur_ctx = ctxRecord as unknown as ContextEntry;
                }
            }
            if (act_context[2]) {
                if (dept > 1) {
                    cur_ctx = Object.values((cur_ctx as ContextEntry).clients!).find(
                        (x) => x.client === act_context[2]
                    )!;
                } else {
                    cur_ctx = ctxRecord[Object.keys(ctxRecord)[0]];
                }
            }
            client.save_context(act_context);
            setActiveContext({
                ctx: act_context,
                refresh: activeContext.refresh,
                properties: (cur_ctx as ContextEntry)?.properties || {},
            });
            setEnableDashboards(true);
            setLayoutState((prevLayoutState) => ({
                ...prevLayoutState,
                staticMenuDesktopInactive: false,
            }));
            setState({
                ready: true,
                loading: false,
                data: ctxRecord,
                selected: act_context.join("#"),
            });
        });
    }, [refreshContext]);

    const get_entry = (
        val: string,
        key: string,
        data: string[],
        icon_href: string | undefined,
        properties: Record<string, unknown>
    ): TreeNode => {
        const icon_data = data.join("-");
        return {
            key: data.join("#"),
            label: val,
            data,
            properties,
            icon_herf: icon_href,
            icon_class: "icon-" + icon_data,
            children: [],
        };
    };

    const get_value = (value: string) => {
        const val = value.split("#");
        setState({
            selected: value,
            data: state.data,
            ready: true,
            loading: false,
        });
        let act_context = val;
        let cur_ctx: ContextEntry | null = null;
        let dept = 0;
        const ctx = state.data!;
        let _ctx: Record<string, ContextEntry> | undefined = ctx;
        while (_ctx) {
            dept++;
            _ctx = _ctx[Object.keys(_ctx)[0]].clients;
        }
        if (act_context) {
            if (!act_context[0]) {
                act_context[0] = "everyware";
            }
            if (dept > 2) {
                cur_ctx = ctx[act_context[0]];
            } else {
                cur_ctx = ctx as unknown as ContextEntry;
            }
        }
        if (act_context[1]) {
            if (dept > 1) {
                if (dept === 2) {
                    cur_ctx = (cur_ctx as unknown as Record<string, ContextEntry>)[act_context[1]];
                } else {
                    cur_ctx = (cur_ctx as ContextEntry).clients![act_context[1]];
                }
            } else {
                cur_ctx = ctx as unknown as ContextEntry;
            }
        }
        if (act_context[2]) {
            if (dept > 1) {
                cur_ctx = Object.values((cur_ctx as ContextEntry).clients!).find(
                    (x) => x.client === act_context[2]
                )!;
            } else {
                cur_ctx = ctx[Object.keys(ctx)[0]];
            }
        }
        setActiveContext({
            ctx: val,
            refresh: activeContext.refresh,
            properties: (cur_ctx as ContextEntry)?.properties || {},
        });
        client.save_context(val);
    };

    if (state.loading) {
        return <Skeleton className="ml-4 w-48 h-8" />;
    }
    if (!state.data || Object.keys(state.data).length === 0) {
        return <></>;
    }

    const treeData: TreeNode[] = [];
    for (const k of Object.keys(state.data)) {
        const provider = state.data[k];
        const ent = get_entry(
            provider.name,
            provider.id,
            [provider.provider, provider.reseller || "", provider.client || ""],
            provider.icon,
            provider.properties
        );
        treeData.push(ent);
        if (provider.clients) {
            for (const k1 of Object.keys(provider.clients)) {
                const reseller = provider.clients[k1];
                const ent1 = get_entry(
                    reseller.name,
                    provider.id + reseller.id,
                    [reseller.provider, reseller.reseller || "", reseller.client || ""],
                    reseller.icon,
                    reseller.properties
                );
                ent.children.push(ent1);
                if (reseller.clients) {
                    for (const k2 of Object.keys(reseller.clients)) {
                        const clientEntry = reseller.clients[k2];
                        const ent2 = get_entry(
                            clientEntry.name,
                            provider.id + reseller.id + clientEntry.id,
                            [clientEntry.provider, clientEntry.reseller || "", clientEntry.client || ""],
                            clientEntry.icon,
                            clientEntry.properties
                        );
                        ent1.children.push(ent2);
                    }
                }
            }
        }
    }

    const flat = flattenNodes(treeData);
    const selectedNode = flat.find((f) => f.node.data.join("#") === state.selected)?.node;

    const iconClasses = flat
        .filter((f) => f.node.icon_herf)
        .map((f) => [f.node.icon_class, f.node.icon_herf]) as [string, string][];

    const iconCss = iconClasses
        .map(
            ([cls, src]) => `.${cls} {
            background-image: url('${src}');
        }`
        )
        .join("\n");

    return (
        <>
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                    .icon-base {
                        display: inline-block;
                        width: calc(1.5rem * var(--topbar-scale));
                        height: calc(1.5rem * var(--topbar-scale));
                        border-radius: 50%;
                        background-repeat: no-repeat;
                        background-size: 100% 100%;
                    }
                    ${iconCss}
                    `,
                }}
            />
            <Popover>
                <PopoverTrigger
                    render={
                        <Button variant="outline" size="sm" className="h-9 gap-2 max-w-64" />
                    }
                >
                    {selectedNode?.icon_herf && (
                        <img
                            src={selectedNode.icon_herf}
                            alt=""
                            style={{
                                borderRadius: "50%",
                                height: "calc(1.5rem * var(--topbar-scale))",
                                width: "calc(1.5rem * var(--topbar-scale))",
                            }}
                        />
                    )}
                    <span className="truncate">{selectedNode?.label}</span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                    <Command>
                        <CommandInput placeholder={_("Select a Profile")} />
                        <CommandList>
                            <CommandEmpty>No profile found.</CommandEmpty>
                            <CommandGroup>
                                {flat.map(({ node, depth }) => (
                                    <CommandItem
                                        key={node.key}
                                        value={node.label}
                                        onSelect={() => {
                                            get_value(node.data.join("#"));
                                        }}
                                        style={{ paddingLeft: `${0.5 + depth * 1}rem` }}
                                        className="gap-2"
                                    >
                                        {node.icon_herf && (
                                            <img
                                                src={node.icon_herf}
                                                alt=""
                                                className="rounded-full flex-shrink-0"
                                                style={{ height: "1.5rem", width: "1.5rem" }}
                                            />
                                        )}
                                        <span className="flex-1 truncate">{node.label}</span>
                                        {state.selected === node.data.join("#") && (
                                            <Check className="h-3.5 w-3.5" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </>
    );
};
